'use client';
import React, { useEffect, useRef } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const BENEFITS = [
  { icon: '⭐', title: 'Priority Access', desc: 'Early entry and reserved spots at events, meetups, and exclusive gatherings.' },
  { icon: '🎟️', title: 'Exclusive Access', desc: 'Invitation-only events, private venues, and members-only experiences.' },
  { icon: '💎', title: 'Discounts & Offers', desc: 'Reduced prices on events, partner services, products, and lifestyle experiences.' },
  { icon: '👑', title: 'VIP Experiences', desc: 'Premium perks like reserved seating, backstage access, and elite networking spaces.' },
  { icon: '🤝', title: 'Premium Networking', desc: 'Connect with a high-quality, purpose-driven community of like-minded individuals.' },
  { icon: '🎯', title: 'Curated Opportunities', desc: 'Access to selected events, collaborations, and opportunities tailored for growth.' },
  { icon: '📚', title: 'Personal Growth', desc: 'Workshops, mentorship, and sessions focused on improving your mindset and skills.' },
  { icon: '💼', title: 'Business Opportunities', desc: 'Exposure to partnerships, job opportunities, and collaborations within the network.' },
  { icon: '🏆', title: 'Member Recognition', desc: 'Opportunities to be featured, lead events, or gain visibility within the community.' },
  { icon: '✈️', title: 'Lifestyle Perks', desc: 'Special deals and access to restaurants, lounges, travel experiences, and more.' },
  { icon: '🛡️', title: 'Community Support', desc: 'A strong network that motivates, supports, and helps you stay accountable.' },
  { icon: '🌟', title: 'Safe & Trusted', desc: 'A secure, respectful environment where members are verified and intentional.' },
];

export default function BenefitsPage() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-up').forEach((el) => el.classList.add('is-visible'));
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <>
      <div className="noise-overlay" aria-hidden="true" />
      <Header />
      <main className="min-h-screen bg-void pt-24 pb-20">
        {/* Radial glow */}
        <div
          className="fixed inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 20%, rgba(123,47,247,0.12) 0%, transparent 70%)' }}
        />

        <div ref={sectionRef} className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
          {/* Page header */}
          <div className="text-center mb-16 fade-up">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[rgba(123,47,247,0.4)] bg-[rgba(123,47,247,0.08)] mb-6">
              <span className="w-2 h-2 rounded-full" style={{ background: '#7B2FF7' }} />
              <span className="text-[11px] font-medium tracking-[0.15em] uppercase" style={{ color: '#7B2FF7' }}>
                Exclusive Membership
              </span>
            </div>
            <h1 className="font-serif font-bold mb-4" style={{ fontSize: 'clamp(2.2rem, 5vw, 4rem)', color: 'var(--cream)' }}>
              Member Privileges &{' '}
              <em className="not-italic" style={{ color: '#7B2FF7' }}>Benefits</em>
            </h1>
            <p className="text-lg font-light max-w-xl mx-auto" style={{ color: 'rgba(240,230,211,0.5)' }}>
              That elevate your everyday lifestyle.
            </p>
          </div>

          {/* Benefits grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
            {BENEFITS?.map((benefit, i) => (
              <div
                key={benefit?.title}
                className={`fade-up delay-${Math.min((i % 3 + 1) * 100, 300)} rounded-2xl p-6 flex flex-col gap-4 group transition-all duration-300`}
                style={{
                  background: '#111124',
                  border: '1px solid rgba(240,230,211,0.08)',
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center text-xl flex-shrink-0 transition-all duration-300"
                    style={{ background: 'rgba(123,47,247,0.12)', border: '1px solid rgba(123,47,247,0.2)' }}
                  >
                    {benefit?.icon}
                  </div>
                  <h3 className="text-sm font-bold" style={{ color: '#7B2FF7' }}>
                    {benefit?.title}
                  </h3>
                </div>
                <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(240,230,211,0.55)' }}>
                  {benefit?.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Closing tagline */}
          <div
            className="fade-up rounded-3xl p-10 text-center"
            style={{ background: 'rgba(123,47,247,0.08)', border: '1px solid rgba(123,47,247,0.25)' }}
          >
            <p className="font-serif font-semibold text-xl md:text-2xl mb-6" style={{ color: 'var(--cream)' }}>
              🔥 "Conect Club isn't just membership —{' '}
              <em className="not-italic" style={{ color: '#7B2FF7' }}>it's a lifestyle upgrade.</em>"
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/verify" className="cta-pill px-8 py-3.5 text-sm font-semibold">
                Verify Your Membership
              </a>
              <a
                href="/register"
                className="px-8 py-3.5 text-sm font-semibold rounded-full transition-all duration-200"
                style={{ border: '1px solid rgba(240,230,211,0.2)', color: 'rgba(240,230,211,0.7)' }}
              >
                Become a Member (Coming Soon)
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
